/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable, OnDestroy } from '@angular/core';
import { UntypedFormBuilder } from '@angular/forms';
import { GroupedTicket } from '@touchplan/ticket';
import { ActualPhase, Side, TicketType } from '@touchplan/common';
import {
	TicketTypeCombined,
	TicketTypeUpdateStatus,
} from 'src/app/app-data/models/ticket-type/TicketTypeExtensions';
import { DateFormControlAdapter } from './DateFormControlAdapter';
import { TicketFormControls } from './ticket-form-controls.enum';
import { ticketFormTypeConfig } from './ticket-form-type-config';
import { validatorHelper } from 'src/app/app-data/validators/validator-helpers';
import { AbstractFormManager } from 'src/app/common/form-utils/abstract-form-manager.service';
import { dateRangeStringValidator, durationHoursValidator } from 'src/app/common/form-utils/validators';
import { Observable } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { GranularEnabledService } from '../../../granular-time/granular-enabled.service';
import ticketFieldsDisable from './ticket-fields-disable.json';
import { ITicketFieldsDisable } from './ticket-fields-disable';

/**
 * This is intended to be a component provided service
 * The lifecycle intent is something like…
 * - the form manager and the form are 1 to 1, so every time a new form is needs, we’ll also need a new form manager.
 *   - technically this is an angular service, and not super conducive to having multiple instances, but the component provider array will
 *     allow for one per component. And absolutely worst case, there's really not much stopping you from manually instantiating an instance.
 * - most likely this means that the normal view has one set and the edit dialog has a second set (each component will need to provide a formManager)
 * - when doing single edit (the only thing supported for now) the form will be recreated each time it’s opened and then closed
 * - when doing a multi edit and switching between types, the form will not be recreated and the fields will just need to be more dynamically swapped around
 */
@Injectable()
export class TicketFormManager
	extends AbstractFormManager<GroupedTicket, TicketTypeCombined, TicketFormControls>
	implements OnDestroy
{
	/** indexed by the "data name" which happens to the same as the name of the string control */
	private dateFormControlAdapters = new Map<string, DateFormControlAdapter>();

	protected formTypeConfig = ticketFormTypeConfig;

	constructor(
		protected override formBuilder: UntypedFormBuilder,
		private granularEnabledService: GranularEnabledService
	) {
		super(formBuilder);
	}

	/**
	 * Update the form with the current ticket
	 * Going to tentatively "not" have this be an observable because it has to be unwrapped to patch to the form anyways.
	 * At the moment I'm inclined to think that this code doesn't even actually need the GroupedTicket aside from this patch.
	 * Probably switch to observable if it does
	 */
	public override patchForm(ticket: GroupedTicket) {
		if (!this.formGroup) {
			return;
		} //no-op probably makes sense in this case

		//resets everything when the type changes, doesn't _really_ matter in the short term, but this seemer safer than nothing
		//does not reset if there was no previous value
		if (this.lastModelUpdate && this.lastModelUpdate.view.type !== ticket.view.type) {
			//explicitly cooerce null/ undefined to Task
			this.changeFormType((ticket.view.type as TicketTypeCombined) || TicketType.Task);
		}

		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7217
		// Combine all these dynamic form changing functions in to one function
		//dynamic form control adding/ removing
		this.updateAssignedAndResponsibleProjectMember(ticket);
		this.showHideVariance(ticket);
		this.showHideGranularFields(ticket, this.granularEnabledService.isGranularEnabledSync);
		this.disableEnableTicketFields(ticket, ticketFieldsDisable as ITicketFieldsDisable);
		// END PLACEHOLDER REPLACE AS PART OF TP-7217

		//odds are high this will need to get more complex at some point
		//but for now every formControl maps 1 to 1 with ticket.view
		//may or may not want the second arg that suppresses the events
		this.formGroup.patchValue(ticket.view, { emitEvent: false });

		if (ticket.view.liveLastPromise?.data.varianceId) {
			const pinData = ticket.view.liveLastPromise?.data;
			const relevantPinData = { varianceId: pinData.varianceId, varianceComment: pinData.varianceComment };

			this.formGroup.patchValue(relevantPinData, { emitEvent: false });
		}

		super.patchForm(ticket);
	}

	/**
	 * Use to update a control in the form as if a user had changed it
	 * Means, it will trigger events, set touched and dirty
	 * The only known use for this is something like the canvas based workdays ui which is...
	 * "conceptually" a form control, but not literally part of the form.
	 * Currently it only supports the liveWorkingDays control, extend to more controls _only as needed_
	 */
	public userFormControlUpdate(controlName: TicketFormControls.liveWorkingDays, value: boolean[]) {
		const control = this.formGroup.get(controlName);
		console.log('control is dirty before', control?.dirty);
		control?.setValue(value, { emitEvent: true });
		// need to explicitly mark the control as dirty and touched
		control?.markAsDirty();
		control?.markAsTouched();
	}

	protected createFormGroup() {
		const formGroup = this.formBuilder.group(
			{
				activityName: [null, validatorHelper('activityName')],
				liveDurationDays: [null, validatorHelper('liveDurationDays')],
				liveCrewSize: [null, validatorHelper('liveCrewSize')],
				liveRole: [null, validatorHelper('liveRole')],
				location: [null],
				externalScheduleId: [null],
				wbsCode: [null],
				livePlanGranularity: ['day'],

				// TASK STUFF
				liveStart: [null, validatorHelper('liveStart')],
				liveFinish: [null, validatorHelper('liveFinish')],
				liveStartTime: ['0:00'],
				liveFinishTime: ['0:00'],
				liveDurationHours: ['00:00:00', durationHoursValidator],

				// MILESTONE STUFF
				// milestoneDate: [ticket?.view.plannedFinish || ticket?.view.plannedStart],
				milestoneType: [null],

				// CONSTRAINT STUFF
				dateRequested: [null],
				// it _may_ make sense to bring these actual versions back for the edit dialog instead of using the live ones
				// plannedFinish: [ticket?.view.lastPlannedFinish || ticket?.view.plannedFinish],
				// actualFinish: [ticket?.view.actualFinish],
				// assignedTo: [this.projectMembers?.find((pm: ProjectMember) => pm.view.roleId === ticket?.view.assignedProjectMemberRoleId)],
				assignedProjectMember: [null],
				responsibleProjectMember: [null],
				// requestedBy: [this.projectMembers?.find((pm: ProjectMember) => pm.view.roleId === ticket?.view.requestedByProjectMemberRoleId)],
				requestedByProjectMember: [null],
				priority: [null],

				//MASTER SCHEDULE STUFF
				masterScheduleLink: [null],

				varianceReason: ['', validatorHelper('varianceReason')],
				varianceComment: [''],

				liveWorkingDays: [null],
				liveWorkSchedule: [null],

				customFields: this.formBuilder.group({}),
			},
			{ validators: [dateRangeStringValidator('liveStart', 'liveFinish')] }
		);

		return formGroup;
	}

	/**
	 * Initializes this service
	 */
	public override create(
		ticketType: TicketTypeCombined = TicketType.Task,
		ticket?: GroupedTicket,
		isDisabled?: boolean
	) {
		const formGroup = super.create(ticketType, ticket, isDisabled);
		// formGroup.setValidators(dateRangeValidator('liveStartAdapter', 'liveFinishAdapter'));

		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7217
		// Combine all these dynamic form changing functions in to one function
		//dynamic form control adding/ removing
		this.updateAssignedAndResponsibleProjectMember(ticket);
		this.showHideVariance(ticket);
		if (ticket) {
			this.showHideGranularFields(ticket, this.granularEnabledService.isGranularEnabledSync);
		}
		// END PLACEHOLDER REPLACE AS PART OF TP-7217

		return formGroup;
	}

	/** form is valid? */
	public isValid$(): Observable<boolean> {
		return this.formGroup.statusChanges.pipe(
			map((status) => status === 'VALID'),
			startWith(this.formGroup.valid),
			distinctUntilChanged()
		);
	}

	/**
	 * Synchronous function to return if the form is valid.
	 */
	public isValidSync(): boolean {
		return this.formGroup.valid;
	}

	/**
	 * Returns an observable that indicates when the form is dirty or not dirty.
	 */
	public isDirty(): Observable<boolean> {
		return this.formGroup.valueChanges.pipe(
			map((_) => this.formGroup.dirty),
			startWith(this.formGroup.dirty),
			distinctUntilChanged()
		);
	}

	/**
	 * Returns true if the form is dirty, false if it's not dirty (no edits made)
	 */
	public isDirtySync(): boolean {
		return this.formGroup.dirty;
	}

	//BEGIN PLACEHOLDER REPLACE AS PART OF TP-7104
	/**
	 * Swaps the assignedProjectMember and responsibleProjectMember formControls in the formGroup based on the context of ticket data
	 */
	private updateAssignedAndResponsibleProjectMember(ticket: GroupedTicket) {
		// TODO - temporarily disable this
		// if (
		// 	!this.shouldDynamicallyManipulateSpecificControls([
		// 		TicketFormControls.assignedProjectMember,
		// 		TicketFormControls.responsibleProjectMember,
		// 	])
		// ) {
		// 	return;
		// }
		// if (!ticket.view.assignedProjectMember && ticket.view.responsibleProjectMember) {
		// 	this.removeFormControl('assignedProjectMember' as TicketFormControls);
		// 	this.addFormControl('responsibleProjectMember' as TicketFormControls, true);
		// }
		// if (!ticket.view.responsibleProjectMember && ticket.view.assignedProjectMember) {
		// 	this.removeFormControl('responsibleProjectMember' as TicketFormControls);
		// 	this.addFormControl('assignedProjectMember' as TicketFormControls, true);
		// }
	}
	//END PLACEHOLDER REPLACE AS PART OF TP-7104

	private showHideVariance(ticket: GroupedTicket) {
		if (
			!this.shouldDynamicallyManipulateSpecificControls([
				TicketFormControls.varianceReason,
				TicketFormControls.varianceComment,
			])
		) {
			return;
		}

		const doesNotHaveVariance = !ticket?.view.liveLastPromise?.data?.varianceId;

		if (ticket.view.needsVariance) {
			this.addFormControl(TicketFormControls.varianceReason, true);
			this.addFormControl(TicketFormControls.varianceComment, true);
			this.controlsAddedOrRemoved.next();
		}
		//remove the variance control if something external cleared the need for variance
		else if (doesNotHaveVariance) {
			this.removeFormControl(TicketFormControls.varianceReason, true);
			this.removeFormControl(TicketFormControls.varianceComment, true);
			this.controlsAddedOrRemoved.next();
		}
	}

	private showHideGranularFields(ticket: GroupedTicket, gtpEnabled: boolean) {
		const ticketType = ticket.view.type ?? TicketType.Task;
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7368
		// Once the durationHours are saved in ticket, we can use that and remove this from ticket model
		const isPlanByHours = ticket.view.livePlanGranularity === 'hour';
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7368
		const isTaskTicket = ticketType === TicketType.Task;

		const changes: Array<{ field: TicketFormControls; enabled: boolean }> = [];
		const isUpdateStatusDialog = (<any>Object.values(TicketTypeUpdateStatus)).includes(this.currentFormType);
		if (!isUpdateStatusDialog) {
			changes.push({ field: TicketFormControls.livePlanGranularity, enabled: gtpEnabled });
			changes.push({
				field: TicketFormControls.liveStartTime,
				enabled: gtpEnabled && isPlanByHours,
			});
			changes.push({
				field: TicketFormControls.liveFinishTime,
				enabled: gtpEnabled && (!isTaskTicket || isPlanByHours),
			});
			changes.push({
				field: TicketFormControls.liveDurationDays,
				enabled: isTaskTicket && !isPlanByHours,
			});
			changes.push({
				field: TicketFormControls.liveFinish,
				enabled: !gtpEnabled || ticketType !== TicketType.Constraint,
			});
			changes.push({
				field: TicketFormControls.liveDurationHours,
				enabled: gtpEnabled && isPlanByHours,
			});
		}

		changes.forEach(({ field, enabled }) => {
			if (this.shouldDynamicallyManipulateSpecificControls([field])) {
				if (enabled) {
					this.addFormControl(field, true, true);
				} else {
					this.removeFormControl(field, true);
				}
			}
		});
		this.controlsAddedOrRemoved.next();
	}

	private disableEnableTicketFields(ticket: GroupedTicket, ticketFieldsDisableConfig: ITicketFieldsDisable) {
		const ticketType = ticket.view.type ?? TicketType.Task;
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7311
		if (ticketType === TicketType.Constraint) {
			// For now, we don't disable any fields for constraints
			// These are more complex and will need to be handled later
			return;
		}
		// END PLACEHOLDER REPLACE AS PART OF TP-7311
		const side = ticket.view.side ?? Side.ACTIVE;
		let phase = ticket.view.actualPhase;
		if (phase === ActualPhase.actualizing) {
			phase = ActualPhase.actualized; // for now, we treat actualizing the same as actualized
		}

		for (const [controlName, disableElement] of Object.entries(ticketFieldsDisableConfig)) {
			const control = this.formGroup.get(controlName as TicketFormControls);
			if (!control) {
				continue;
			}

			// check if the control should be disabled based on the ticket type and phase
			// or if the control should be disabled based on the ticket type and side
			const shouldDisable = disableElement.disabled.some(
				(disableConfig) =>
					(disableConfig.ticketType === ticketType && disableConfig.phase === phase) ||
					(disableConfig.ticketType === ticketType && disableConfig.side === side)
			);

			if (shouldDisable) {
				control.disable();
			} else if (control.disabled) {
				control.enable();
			}
		}
	}

	protected getControlDataFromModel(controlName: TicketFormControls, model: GroupedTicket) {
		return model.view[controlName]; //null checked in parent
	}

	public override ngOnDestroy() {
		for (const [key, adapter] of this.dateFormControlAdapters) {
			adapter.destroy();
		}
		super.ngOnDestroy();
	}
}
